import React, { type FunctionComponent, useState } from 'react'
import { NotificationLevel, notify } from '@retailer-platform/shared-components'
import { ConfirmIcon, IssueIcon } from '@instacart/ids-core'
import { useSelectionManager } from 'utils/selection-manager/SelectionManager'
import { type ProblemType } from '../../../../api'
import useCreateCatalogProblemTrackerTicket from '../../../../api/createCatalogProblemTrackerTicket.hooks'
import { type ProductSearchParams } from '../../utils/types'
import { useDomainAccessControl } from '../../../../utils/domain/accessControl'
import { productSearchCPTButton } from '../../../../access-control/searchAccess.configuration'
import ReportAProblemButton from './ReportAProblemButton'
import ReportAProblemModal from './ReportAProblemModal'

export interface ReportAProblemFormValues {
  description: string | null
  problemType: ProblemType | null
}

interface ReportAProblemState {
  values: ReportAProblemFormValues
  showModal: boolean
}

const DEFAULT_STATE: ReportAProblemState = {
  values: {
    description: null,
    problemType: null,
  },
  showModal: false,
}

interface ProductsSearchReportAProblemProps {
  searchParams: ProductSearchParams
}

const ProductsSearchReportAProblemWrapper: FunctionComponent<ProductsSearchReportAProblemProps> = ({
  searchParams,
}: ProductsSearchReportAProblemProps) => {
  const hasAccess = useDomainAccessControl()
  const allowed = hasAccess(productSearchCPTButton)
  return allowed ? <ProductsSearchReportAProblem searchParams={searchParams} /> : null
}

const ProductsSearchReportAProblem: FunctionComponent<ProductsSearchReportAProblemProps> = ({
  searchParams,
}: ProductsSearchReportAProblemProps) => {
  const [{ values, showModal }, setState] = useState<ReportAProblemState>(DEFAULT_STATE)
  const { selectedCount, selectedKeys } = useSelectionManager()

  const { cptTicketId, loading, error, trigger } = useCreateCatalogProblemTrackerTicket({
    searchParams: searchParams,
    productIds: selectedKeys,
    description: values.description,
    problemType: values.problemType,
  })

  const onModalClose = React.useCallback(
    () =>
      setState({
        values: { problemType: values.problemType, description: values.description },
        showModal: false,
      }),
    [setState, values.problemType, values.description]
  )

  const onModalOpen = React.useCallback(
    () =>
      setState({
        values: { problemType: values.problemType, description: values.description },
        showModal: true,
      }),
    [setState, values.problemType, values.description]
  )

  React.useEffect(() => {
    if (cptTicketId && !error && !loading) {
      notify({
        icon: <ConfirmIcon />,
        contents: 'Problem successfully reported. Ticket ID is ' + cptTicketId,
      })
      setState(DEFAULT_STATE)
    }
    if (error && !loading) {
      notify({
        icon: <IssueIcon />,
        level: NotificationLevel.Error,
        contents: 'A problem occured logging your ticket: ' + error,
      })
    }
  }, [cptTicketId, error, loading])

  const onValuesChange = React.useCallback(
    (values: ReportAProblemFormValues) => {
      setState({ showModal: showModal, values: values })
    },
    [setState, showModal]
  )

  return (
    <React.Fragment>
      <ReportAProblemButton
        onClick={onModalOpen}
        productCount={selectedCount}
        disabled={selectedCount > 1000}
      />
      {showModal && (
        <ReportAProblemModal
          productCount={selectedCount}
          values={values}
          isSubmitting={loading}
          onClose={onModalClose}
          onChange={onValuesChange}
          onSubmit={trigger}
        />
      )}
    </React.Fragment>
  )
}

export default ProductsSearchReportAProblemWrapper
